import { ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import type { MessageContentAddResponse, MessageReactionAddResponse, MessageResponse, StarterBlockData } from '~/models/chat';

export function useChat() {
  const BASE_URL = useRuntimeConfig().public.wsUrl;
  const IDA_URL = useRuntimeConfig().public.idaUrl;
  const IDA_WS_URL = useRuntimeConfig().public.idaWsUrl;
  const IDA_PROXY = useRuntimeConfig().public.idaProxy;

  const { locale, t } = useI18n();

  const chatStore = useChatStore();
  const { messages } = storeToRefs(chatStore);

  const ws = ref<WebSocket | null>(null);
  const channelId = ref<string | null>(null);
  const code = ref<string | null>(null);
  const token = ref<string | null>(null);
  const isConnected = ref(false);
  const starterBlockData = ref<StarterBlockData[]>([]);

  const panicMode = ref<boolean>(false);
  const panicModeText = ref<string>('panic-mode');
  const canWrite = ref(true);

  async function connect() {
    messages.value = [];

    try {
      // Get code, cookies, and token  from IDA
      let config: any = null;

      if (IDA_PROXY) {
        const { code: c } = await $fetch('/chat/auth');

        code.value = c;

        config = await $fetch('/chat/config');
      } else {
        config = await $fetch(IDA_URL + '/api/config');
        const { token: t, code: c } = await $fetch<{ token: string; code: string }>(IDA_URL + '/api/auth/guest-token');
        code.value = c;
        token.value = t;
      }

      if (config) {
        const translations: any[] = config.translations;
        const currentTranslation = translations.find((translation) => translation.item_name == locale.value);
        panicMode.value = config.panic_mode_enabled ?? false;
        panicModeText.value = config.dynamic_system_message_contents.find((item: any) => item.item_name == locale.value)?.panic_mode ?? 'panic-mode';

        if (currentTranslation) {
          starterBlockData.value = currentTranslation.examples?.map((example: string) => ({
            title: example
          }));
        }
      }

      // Connect to Nuxt WebSocket proxy
      if (IDA_PROXY) {
        ws.value = new WebSocket(`${BASE_URL}/chat/ws?code=${code.value}`);
      } else {
        ws.value = new WebSocket(`${IDA_WS_URL}/api/ws?code=${code.value}`);
      }

      ws.value.onopen = () => {
        isConnected.value = true;
        // console.log('[Client] Connected to Nuxt WS proxy.');
      };

      ws.value.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.event === 'CHANNEL_CREATE') {
          channelId.value = data.id;
          chatStore.setChannelId(channelId.value!);
        } else if (data.event === 'MESSAGE_CREATE') {
          handleMassageCreation(data);
        } else if (data.event === 'MESSAGE_UPDATE') {
          handleMessageUpdate(data);
        } else if (data.event === 'MESSAGE_CONTENT_ADD') {
          handleMessageContentAdd(data);
        } else if (data.event === 'MESSAGE_REACTION_ADD') {
          handleMessageReactionAdd(data);
        }
      };

      ws.value.onclose = (event) => {
        console.log(`[System] Disconnected from Nuxt chat proxy (Code: ${event.code}, Reason: ${event.reason}).`);
        isConnected.value = false;
        console.log('[Client] Disconnected from Nuxt WS proxy.');
        // TODO: Reconnect or error message?
      };

      ws.value.onerror = (error) => {
        console.log('[System] WebSocket Error occurred!');
        isConnected.value = false;
        console.error('[Client] WebSocket Error:', error);

        panicMode.value = true;
        panicModeText.value = t('chat.connectivityError');
      };
    } catch {
      panicMode.value = true;
      panicModeText.value = t('chat.connectivityError');
    }
  }

  function disconnect() {
    ws.value?.close();
    channelId.value = null;

    chatStore.setChannelId(undefined);
    chatStore.stopPolling();
  }

  async function sendMessage(content: string) {
    if (!channelId.value) return;

    canWrite.value = false;
    const nonce = uuidv4();
    if (IDA_PROXY) {
      await $fetch('/chat/send', {
        method: 'POST',
        body: { channelId: channelId.value, content }
      });

      await chatStore.searchByChatMessage(content, nonce);
    } else {
      await $fetch(IDA_URL + '/api/channels/' + encodeURIComponent(channelId.value) + '/messages', {
        method: 'POST',
        body: { content: content, nonce: nonce },
        headers: { Authorization: `Bearer ${token.value}` }
      });

      await chatStore.searchByChatMessage(content, nonce);
    }
  }

  async function sendReaction(messageId: string, emojiStr: string) {
    if (!channelId.value) return;

    canWrite.value = false;

    if (IDA_PROXY) {
      await $fetch('/chat/send_reaction', {
        method: 'POST',
        body: { channelId: channelId.value, messageId, emojiStr }
      });
    } else {
      await $fetch(
        IDA_URL +
          `/api/channels/${encodeURIComponent(channelId.value)}/messages/${encodeURIComponent(messageId)}/reactions/${encodeURIComponent(emojiStr)}/@me`,
        {
          method: 'PUT',
          body: { channelId: channelId.value, messageId, emojiStr },
          headers: { Authorization: `Bearer ${token.value}` }
        }
      );
    }
  }

  function handleMassageCreation(data: MessageResponse) {
    if (hasMessage(data.id)) return;

    messages.value.push({
      ...data
    });
  }

  function handleMessageUpdate(data: MessageResponse) {
    const index = messages.value.findIndex((msg) => msg.id === data.id);

    if (index !== -1) {
      messages.value[index] = {
        ...messages.value[index],
        ...data
      };
    }

    canWrite.value = true;
  }

  function handleMessageContentAdd(data: MessageContentAddResponse) {
    const index = messages.value.findIndex((msg) => msg.id === data.message_id);

    if (index !== -1) {
      messages.value[index] = {
        ...messages.value[index],
        content: messages.value[index].content + data.content
      };
    }
  }

  function handleMessageReactionAdd(data: MessageReactionAddResponse) {
    const index = messages.value.findIndex((msg) => msg.id === data.message_id);

    if (index !== -1) {
      messages.value[index] = {
        ...messages.value[index],
        emojis: data.emoji
      };
    }

    canWrite.value = true;
  }

  function hasMessage(id: string): boolean {
    return messages.value.some((msg) => msg.id === id);
  }

  function removeMessage(id: string) {
    const index = messages.value.findIndex((msg) => msg.id === id);
    if (index === -1) return;

    messages.value.splice(index, 1);
  }

  return {
    messages,
    isConnected,
    starterBlockData,
    panicMode,
    panicModeText,
    canWrite,
    sendReaction,
    disconnect,
    connect,
    sendMessage,
    removeMessage
  };
}
